import { createApp } from 'vue'
import App from './App.vue'
import router from './router';

import { IonicVue } from '@ionic/vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { queryClient } from '@/shared/queryClient';
import { createPinia } from 'pinia';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

import './resources/css/float-element.css';

/* Theme variables */
import './theme/variables.css';

/* Vivid design tokens */
import '@vonage/vivid/styles/tokens/theme-light.css';
import '@vonage/vivid/styles/core/theme.css';

/* Custom css */
import './resources/css/common.css';

/* Vivid component overrides */
import './resources/css/vivid-overrides.css';

/* Grid layout utilities (ion-grid replacement) */
import './resources/css/grid-layout.css';

/* Typography utilities */
import './resources/css/typography.css';

/* Spacing utilities */
import './resources/css/spacing.css';

/* Layout */
import MasterLayout from './components/MasterLayout.vue';

/* Common components */
import {
  IonItem,IonLabel,IonInput,IonList,IonListHeader,IonAvatar,IonGrid,IonCol,IonRow,IonItemGroup,IonItemDivider,IonChip,IonButton,IonLoading,IonHeader,IonFooter,IonToolbar,IonButtons,IonIcon,IonRefresher,IonRefresherContent,IonContent,IonModal,IonTitle,IonPage,IonCard,IonCardTitle,IonCardSubtitle,IonCardHeader,IonCardContent,IonThumbnail,IonActionSheet,IonDatetime,IonSegment,IonSegmentButton,IonText,IonAlert,IonBadge,IonImg,IonSpinner,IonSkeletonText
} from "@ionic/vue";

/* Custom components */
import myInput from './components/myInput.vue';
import Pagination from "/src/components/pagination.vue";
import pageAccessNotAllowed from './components/pageAccessNotAllowed.vue';

import './registerServiceWorker'

import { install } from "vue3-recaptcha-v2";
import { environment } from '/src/shared/env';
import { useAuthStore } from '@/stores';
import { setAuthStore } from '@/shared/apiInterceptor';

/* Vivid Vue components */
import {
  VButton, VIcon, VBadge, VTag, VTextField,VContextualHelp, VTextArea, VSelect, VOption,
  VCheckbox, VSwitch, VRadio, VRadioGroup, VDialog, VBanner, VProgressRing,
  VProgress, VNav, VNavItem, VAvatar, VCard, VAccordion, VAccordionItem,
  VTabs, VTab, VTabPanel, VPopover, VNote, VTooltip, VSearchableSelect,
  VDatePicker, VDateTimePicker, VSlider, VTagGroup, VHeader, VDivider, VEmptyState, VPagination
} from '@vonage/vivid-vue';

const app = createApp(App);
const pinia = createPinia();

app.use(install, {
  sitekey: environment("VUE_APP_GOOGLE_RECAPTCHA_KEY"),
  cnDomains: false
})
app.use(IonicVue);
app.use(pinia);
// Inject the auth store into the API interceptor immediately after Pinia is
// activated. This guarantees the store is available for any API call that
// fires during startup (route guards, TanStack Query prefetches, etc.).
setAuthStore(useAuthStore());
app.use(router);
app.use(VueQueryPlugin, { queryClient });

app.component('master-layout', MasterLayout);
app.component('ion-item', IonItem);
app.component('ion-label', IonLabel);
app.component('ion-input', IonInput);
app.component('ion-list', IonList);
app.component('ion-list-header', IonListHeader);
app.component('ion-avatar', IonAvatar);
app.component('ion-grid', IonGrid);
app.component('ion-col', IonCol);
app.component('ion-row', IonRow);
app.component('ion-item-group', IonItemGroup);
app.component('ion-item-divider', IonItemDivider);
app.component('ion-chip', IonChip);
app.component('ion-button', IonButton);
app.component('ion-loading', IonLoading);
app.component('ion-header', IonHeader);
app.component('ion-footer', IonFooter);
app.component('ion-toolbar', IonToolbar);
app.component('ion-buttons', IonButtons);
app.component('ion-refresher', IonRefresher);
app.component('ion-refresher-content', IonRefresherContent);
app.component('ion-content', IonContent);
app.component('ion-modal', IonModal);
app.component('ion-title', IonTitle);
app.component('ion-page', IonPage);
app.component('ion-card', IonCard);
app.component('ion-card-title', IonCardTitle);
app.component('ion-card-subtitle', IonCardSubtitle);
app.component('ion-card-header', IonCardHeader);
app.component('ion-card-content', IonCardContent);
app.component('ion-thumbnail', IonThumbnail);
app.component('ion-action-sheet', IonActionSheet);
app.component('ion-datetime', IonDatetime);
app.component('ion-segment', IonSegment);
app.component('ion-segment-button', IonSegmentButton);
app.component('ion-text', IonText);
app.component('ion-alert', IonAlert);
app.component('ion-badge', IonBadge);
app.component('ion-img', IonImg);
app.component('ion-spinner', IonSpinner);
app.component('ion-skeleton-text', IonSkeletonText);

app.component('my-input', myInput);
app.component('page-access-not-allowed', pageAccessNotAllowed);
app.component('pagination', Pagination);

// Vivid Vue component registrations (PascalCase + kebab-case for universal resolution)
app.component('VButton', VButton);
app.component('v-button', VButton);
app.component('VIcon', VIcon);
app.component('v-icon', VIcon);
app.component('VBadge', VBadge);
app.component('VTag', VTag);
app.component('VTextField', VTextField);
app.component('VContextualHelp', VContextualHelp);
app.component('VTextArea', VTextArea);
app.component('VSelect', VSelect);
app.component('VOption', VOption);
app.component('VCheckbox', VCheckbox);
app.component('VSwitch', VSwitch);
app.component('v-switch', VSwitch);
app.component('VRadio', VRadio);
app.component('VRadioGroup', VRadioGroup);
app.component('VDialog', VDialog);
app.component('v-dialog', VDialog);
app.component('VBanner', VBanner);
app.component('VProgressRing', VProgressRing);
app.component('VProgress', VProgress);
app.component('VNav', VNav);
app.component('VNavItem', VNavItem);
app.component('VAvatar', VAvatar);
app.component('VCard', VCard);
app.component('VAccordion', VAccordion);
app.component('VAccordionItem', VAccordionItem);
app.component('VTabs', VTabs);
app.component('VTab', VTab);
app.component('VTabPanel', VTabPanel);
app.component('VPopover', VPopover);
app.component('VNote', VNote);
app.component('VTooltip', VTooltip);
app.component('VSearchableSelect', VSearchableSelect);
app.component('VDatePicker', VDatePicker);
app.component('VDateTimePicker', VDateTimePicker);
app.component('VSlider', VSlider);
app.component('VTagGroup', VTagGroup);
app.component('VHeader', VHeader);
app.component('VDivider', VDivider);
app.component('VEmptyState', VEmptyState);
app.component('VPagination', VPagination);

router.isReady().then(() => {
  app.mount('#app');
});